import { useCallback, useEffect, useMemo, useRef } from 'react';

import { appConfig } from '../../../appConfig';
import { cleanupAbTests, isAbTestPreview, setupAbTest } from '../../../utility/abTests';
import { useAppDispatch, useAppSelector } from '../../common/hooks/useThunkDispatch';
import { debugSelectors } from '../../common/selectors/debugSelectors';
import { debugActions } from '../../common/store/slices/ui/debugSlice';
import gaTracking from '../../common/tracking/tracking';

/**
 * This hook makes the user responsible for triggering interaction tracking
 */
export const useImperativeAbTest = (args: {
    name: string;
    contentId?: string;
    userId?: string;
}) => {
    const { name, contentId, userId } = args;

    const { isVariant: isResultVariant, isEpiEnabled } = useMemo(() => {
        const epiAbTest = appConfig.coopSettings.featureFlags.abTests
            ?.filter((item) => !!item.enabled)
            .find(
                (t) =>
                    (t.contentId.length === 0 && t.name === name) ||
                    (t.contentId.length !== 0 &&
                        t.name === name &&
                        !!contentId &&
                        t.contentId.includes(contentId)),
            );

        let isVariant = false;
        let isEnabled = false;

        if (epiAbTest) {
            const abTest = setupAbTest(epiAbTest, userId);
            isVariant = abTest.abTestValue === '1';
            isEnabled = true;
        }

        return { isVariant, isEpiEnabled: isEnabled };
    }, [contentId, name, userId]);

    const trackInteraction = useCallback(
        (variant: string) => {
            // eslint-disable-next-line no-console
            console.logDev(`Triggered ${name} AB test. Result: ${variant}`);

            gaTracking.abTest.userInteraction(name, variant, 300);
        },
        [name],
    );

    return { isVariant: isResultVariant, trackInteraction, isEpiEnabled };
};

/**
 * This hook will automatically trigger the test when component loads
 */
export const useAbTest = ({
    name,
    contentId,
    userId,
    enabled,
    trackOnce = false,
}: {
    name: string;
    contentId?: string;
    userId?: string;
    enabled: boolean;
    trackOnce?: boolean;
}) => {
    const isPreview =
        useAppSelector((state) => debugSelectors.selectAbTestPreviewById(state, name)) ||
        isAbTestPreview();

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(debugActions.setAbTestPreview({ abTestName: name, value: false }));
    }, [dispatch, name]);

    const { isVariant, isEpiEnabled, trackInteraction } = useImperativeAbTest({
        name,
        contentId,
        userId,
    });

    const alreadyTrackedRef = useRef(false);

    const trackAbTest = useCallback(
        (variant: string) => {
            if (!trackOnce) {
                trackInteraction(variant);
                return;
            }

            if (!alreadyTrackedRef.current) {
                alreadyTrackedRef.current = true;
                trackInteraction(variant);
            }
        },
        [trackInteraction, trackOnce],
    );

    useEffect(() => {
        cleanupAbTests();
    }, []);

    useEffect(() => {
        if (isEpiEnabled && enabled) {
            if (isVariant) {
                trackAbTest('B');
            } else {
                trackAbTest('A');
            }
        }
    }, [trackAbTest, isEpiEnabled, enabled, isVariant, name, contentId]);

    // to become variant you need to check all flags. We dont want to show variant if someone had previously saved abtest "B" and the test is disabled
    return { isVariant: isPreview || (isEpiEnabled && enabled && isVariant), isEpiEnabled };
};
